import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { RouteSkeleton } from './RouteSkeleton';
import { ForbiddenState } from './ForbiddenState';
import type { ForbiddenReason } from '../lib/constants/forbiddenReasons';
import { LOGIN_ROUTE } from '../lib/constants/authRoutes';
import type { UserRole } from '../lib/constants/roles';
import { useShallow } from 'zustand/react/shallow';

interface RoleGuardProps {
  allowedRoles: readonly UserRole[];
  /** Which explanation the forbidden screen shows; each guard knows who its routes are for. */
  forbiddenReason: ForbiddenReason;
}

/**
 * Auth + role guard that renders <Outlet/> for use as a parent route element.
 * Keeps loading state explicit while the Zustand persist middleware rehydrates:
 * if a token exists but user has not yet been restored from localStorage, show
 * RouteSkeleton rather than a premature redirect to /login. A role mismatch shows
 * the forbidden screen, never a redirect (AB#2138).
 */
export const RoleGuard = ({ allowedRoles, forbiddenReason }: RoleGuardProps) => {
  const { isAuthenticated, user, sessionRestoring } = useAuthStore(useShallow((s) => ({ isAuthenticated: s.isAuthenticated, user: s.user, sessionRestoring: s.sessionRestoring })));
  const location = useLocation();

  // Startup refresh in flight (lib/api.ts restoreSession): neither redirect nor render the page,
  // the token it needs does not exist yet. The outcome re-renders this guard.
  if (sessionRestoring) return <RouteSkeleton />;

  if (!isAuthenticated) {
    // Carries the page the visitor was actually trying to reach across the login round-trip
    // (AB#1864) — without it, a seller arriving unauthenticated from an external redirect (e.g.
    // the Shopify OAuth callback) lands on /login, then bounces to the generic /dashboard after
    // signing in and never reaches the page that redirect was for. LoginPage re-validates this
    // value with sanitizeReturnTo before ever navigating to it — see lib/returnTo.ts.
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`${LOGIN_ROUTE}?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  // user not yet hydrated from persisted store — hold on
  if (!user) return <RouteSkeleton />;
  if (!allowedRoles.includes(user.role)) return <ForbiddenState reason={forbiddenReason} />;

  return <Outlet />;
};

export default RoleGuard;
